import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';

import axiosInstance from '../../api/axiosConfig';

const Admissions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [selectedAdmission, setSelectedAdmission] =
    useState(null);

  const [openDialog, setOpenDialog] =
    useState(false);

  useEffect(() => {
    fetchAdmissions();
  }, []);

  const fetchAdmissions = async () => {
    try {
      setLoading(true);

      const response =
        await axiosInstance.get('/admission');

      const data =
        response.data?.data ||
        response.data ||
        [];

      setAdmissions(Array.isArray(data) ? data : []);
      setError('');
    }
    catch (err) {
      console.error(err);
      setError('Failed to load admissions');
    }
    finally {
      setLoading(false);
    }
  };

  const handleView = (admission) => {
    setSelectedAdmission(admission);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAdmission(null);
  };

  const handleApprove = async (id) => {
    try {
      await axiosInstance.patch(
        `/admission/${id}/approve`
      );

      setSuccess('Admission approved successfully');

      fetchAdmissions();
    }
    catch (err) {
      console.error(err);
      setError('Failed to approve admission');
    }
  };

  const handleDownloadReceipt = async (
  applicationNumber
) => {
  try {

    const response =
      await axiosInstance.get(
        `/public/application-receipt/${applicationNumber}`,
        {
          responseType: 'blob'
        }
      );

    const url =
      window.URL.createObjectURL(
        new Blob([response.data])
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download =
      `AdmissionReceipt_${applicationNumber}.pdf`;

    document.body.appendChild(link);

    link.click();

    link.remove();

  } catch {

    setError(
      'Unable to download receipt'
    );

  }
};

  const handleReject = async (id) => {
    try {
      await axiosInstance.patch(
        `/admission/${id}/reject`
      );

      setSuccess('Admission rejected successfully');

      fetchAdmissions();
    }
    catch (err) {
      console.error(err);
      setError('Failed to reject admission');
    }
  };

  const filteredAdmissions = useMemo(() => {
    return admissions.filter((item) => {

      const matchesSearch =
        item.fullName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.mobile
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'All'
          ? true
          : item.applicationStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [
    admissions,
    searchTerm,
    statusFilter
  ]);

  const totalApplications =
    admissions.length;

  const pendingCount =
    admissions.filter(
      x => x.applicationStatus === 'Pending'
    ).length;

  const approvedCount =
    admissions.filter(
      x => x.applicationStatus === 'Approved'
    ).length;

  const rejectedCount =
    admissions.filter(
      x => x.applicationStatus === 'Rejected'
    ).length;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';

      case 'Rejected':
        return 'error';

      default:
        return 'warning';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 6
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      <Typography
        variant="h4"
        fontWeight="bold"
        mb={3}
      >
        Admission Management
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {/* Dashboard Cards */}

      <Grid container spacing={3} mb={3}>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">
                Total Applications
              </Typography>

              <Typography
                variant="h4"
                fontWeight="bold"
              >
                {totalApplications}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">
                Pending
              </Typography>

              <Typography
                variant="h4"
                color="warning.main"
                fontWeight="bold"
              >
                {pendingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">
                Approved
              </Typography>

              <Typography
                variant="h4"
                color="success.main"
                fontWeight="bold"
              >
                {approvedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">
                Rejected
              </Typography>

              <Typography
                variant="h4"
                color="error.main"
                fontWeight="bold"
              >
                {rejectedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
           

      <Card sx={{ mb: 3 }}>
        <CardContent>

          <Grid container spacing={2}>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by Name or Mobile"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>

                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) =>
                    setStatusFilter(e.target.value)
                  }
                >
                  <MenuItem value="All">
                    All
                  </MenuItem>

                  <MenuItem value="Pending">
                    Pending
                  </MenuItem>

                  <MenuItem value="Approved">
                    Approved
                  </MenuItem>

                  <MenuItem value="Rejected">
                    Rejected
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchAdmissions}
                sx={{ height: '56px' }}
              >
                Refresh
              </Button>
            </Grid>

          </Grid>

        </CardContent>
      </Card>

      {/* Table */}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflowX: 'auto'
        }}
      >
        <Table>

          <TableHead>

            <TableRow
              sx={{
                backgroundColor:
                  theme.palette.primary.main
              }}
            >
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 700
                }}
              >
                App No
              </TableCell>

              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 700
                }}
              >
                Student
              </TableCell>

              {!isMobile && (
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 700
                  }}
                >
                  Mobile
                </TableCell>
              )}

              {!isMobile && (
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 700
                  }}
                >
                  Course
                </TableCell>
              )}

              {!isMobile && (
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 700
                  }}
                >
                  Branch
                </TableCell>
              )}
              <TableCell
  sx={{
    color: '#fff',
    fontWeight: 700
  }}
>
  UTR Number
</TableCell>

              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 700
                }}
              >
                Status
              </TableCell>

              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 700
                }}
              >
                Payment
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  color: '#fff',
                  fontWeight: 700
                }}
              >
                Actions
              </TableCell>

            </TableRow>

          </TableHead>

          <TableBody>

            {filteredAdmissions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                >
                  No admissions found
                </TableCell>
              </TableRow>
            ) : (
              filteredAdmissions.map(
                (admission) => (
                  <TableRow
                    key={
                      admission.applicationId
                    }
                    hover
                  >
                    <TableCell>
                      {
                        admission.applicationNumber
                      }
                    </TableCell>

                    <TableCell>
                      {admission.fullName}
                    </TableCell>

                    {!isMobile && (
                      <TableCell>
                        {admission.mobile}
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell>
                        {
                          admission.course
                            ?.courseName
                        }
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell>
                        {
                          admission.branch
                            ?.branchName
                        }
                      </TableCell>
                    )}
                    <TableCell>
  {admission.paymentReference || '-'}
</TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          admission.applicationStatus
                        }
                        color={getStatusColor(
                          admission.applicationStatus
                        )}
                      />
                    </TableCell>

                    <TableCell>

                      <Chip
  size="small"
  label={admission.paymentStatus}
  color={
    admission.paymentStatus === 'Paid'
      ? 'success'
      : admission.paymentStatus === 'Rejected'
      ? 'error'
      : admission.paymentStatus === 'Submitted'
      ? 'info'
      : 'warning'
  }
/>

                    </TableCell>

                    <TableCell align="center">

                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >

                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleView(
                              admission
                            )
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>

                        {admission.applicationStatus ===
                          'Pending' && (
                          <>
                            <IconButton
                              color="success"
                              onClick={() =>
                                handleApprove(
                                  admission.applicationId
                                )
                              }
                            >
                              <CheckCircleIcon />
                            </IconButton>

                            <IconButton
                              color="error"
                              onClick={() =>
                                handleReject(
                                  admission.applicationId
                                )
                              }
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        )}

                      </Stack>

                    </TableCell>

                  </TableRow>
                )
              )
            )}

          </TableBody>

        </Table>
      </TableContainer>
            {/* View Details Dialog */}

     <Dialog
  open={openDialog}
  onClose={handleCloseDialog}
  maxWidth="md"
  fullWidth
>
  <DialogTitle sx={{ fontWeight: 700 }}>
    Admission Details
  </DialogTitle>

  <DialogContent dividers>

    {selectedAdmission && (

      <Grid container spacing={3}>

        {/* Student Created Alert */}

        {selectedAdmission.student && (
          <Grid item xs={12}>
            <Alert severity="success">
              Student record has been created successfully.
            </Alert>
          </Grid>
        )}

        {/* Personal Information */}

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Personal Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Application No:</strong> {selectedAdmission.applicationNumber}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Full Name:</strong> {selectedAdmission.fullName}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Father Name:</strong> {selectedAdmission.fatherName}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Mother Name:</strong> {selectedAdmission.motherName}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>DOB:</strong> {selectedAdmission.dob}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Gender:</strong> {selectedAdmission.gender}</Typography>
        </Grid>

        {/* Registration Details */}

        {selectedAdmission.student && (
          <>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mt: 2 }}
              >
                Registration Details
              </Typography>

              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>

                  <Grid container spacing={2}>

                    <Grid item xs={12} md={4}>
                      <Typography>
                        <strong>Student ID:</strong>
                        {' '}
                        {selectedAdmission.student.studentId}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography>
                        <strong>Registration No:</strong>
                        {' '}
                        {selectedAdmission.student.registrationNumber}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography>
                        <strong>Registration ID:</strong>
                        {' '}
                        {selectedAdmission.student.registrationId}
                      </Typography>
                    </Grid>

                  </Grid>

                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Contact Information */}

        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mt: 2 }}
          >
            Contact Information
          </Typography>

          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Mobile:</strong> {selectedAdmission.mobile}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Email:</strong> {selectedAdmission.email || '-'}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography><strong>Address:</strong> {selectedAdmission.address}</Typography>
        </Grid>

        {/* Academic Information */}

        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mt: 2 }}
          >
            Academic Information
          </Typography>

          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Qualification:</strong> {selectedAdmission.qualification}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography><strong>Aadhaar:</strong> {selectedAdmission.aadhaarNumber}</Typography>
        </Grid>

        {/* Course Information */}

        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mt: 2 }}
          >
            Course Information
          </Typography>

          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography>
            <strong>Course:</strong>
            {' '}
            {selectedAdmission.course?.courseName}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography>
            <strong>Duration:</strong>
            {' '}
            {selectedAdmission.course?.durationMonths}
            {' '}Months
          </Typography>
        </Grid>

        {/* Branch Details */}

        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mt: 2 }}
          >
            Branch Details
          </Typography>

          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>

              <Typography>
                <strong>Branch:</strong>
                {' '}
                {selectedAdmission.branch?.branchName}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>Director:</strong>
                {' '}
                {selectedAdmission.branch?.directorName || '-'}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>Branch Head:</strong>
                {' '}
                {selectedAdmission.branch?.branchHeadName || '-'}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>Primary Phone:</strong>
                {' '}
                {selectedAdmission.branch?.primaryPhone || '-'}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>WhatsApp:</strong>
                {' '}
                {selectedAdmission.branch?.whatsAppNumber || '-'}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>Address:</strong>
                {' '}
                {selectedAdmission.branch?.address || '-'}
              </Typography>

            </CardContent>
          </Card>
        </Grid>

        {/* Status */}

<Grid item xs={12}>
  <Typography
    variant="h6"
    fontWeight="bold"
    sx={{ mt: 2 }}
  >
    Application Status
  </Typography>

  <Divider sx={{ mb: 2 }} />
</Grid>

{/* Application Status */}

<Grid item xs={12} md={6}>
  <Typography mb={1}>
    <strong>Application Status:</strong>
  </Typography>

  <Chip
    label={
      selectedAdmission.applicationStatus === "Approved"
        ? "Approved - Student Created & Registration Generated"
        : selectedAdmission.applicationStatus === "Rejected"
        ? "Rejected - Admission Not Approved"
        : "Pending - Waiting For Admin Review"
    }
    color={getStatusColor(selectedAdmission.applicationStatus)}
    sx={{ fontWeight: 600 }}
  />
</Grid>

{/* Payment Status */}

<Grid item xs={12} md={6}>
  <Typography mb={1}>
    <strong>Payment Status:</strong>
  </Typography>

  <Chip
    label={
      selectedAdmission.paymentStatus === "Paid"
        ? "Paid - Payment Verified Successfully"
        : selectedAdmission.paymentStatus === "Submitted"
        ? "Submitted - Waiting For Admin Verification"
        : selectedAdmission.paymentStatus === "Rejected"
        ? "Rejected - Payment Verification Failed"
        : "Pending - Payment Yet To Be Submitted"
    }
    color={
      selectedAdmission.paymentStatus === "Paid"
        ? "success"
        : selectedAdmission.paymentStatus === "Submitted"
        ? "info"
        : selectedAdmission.paymentStatus === "Rejected"
        ? "error"
        : "warning"
    }
    sx={{ fontWeight: 600 }}
  />
</Grid>

{/* Payment Details */}

<Grid item xs={12}>
  <Typography
    variant="h6"
    fontWeight="bold"
    sx={{ mt: 2 }}
  >
    Payment Details
  </Typography>

  <Divider sx={{ mb: 2 }} />
</Grid>

<Grid item xs={12} md={6}>
  <Typography>
    <strong>UTR / Transaction Number:</strong>{" "}
    {selectedAdmission.paymentReference || "-"}
  </Typography>
</Grid>

<Grid item xs={12} md={6}>
  <Typography>
    <strong>Payment Submitted On:</strong>{" "}
    {selectedAdmission.paymentStatus === "Submitted"
      ? new Date(
          selectedAdmission.updatedAt
        ).toLocaleString()
      : "-"}
  </Typography>
</Grid>

        {/* Timeline */}

        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mt: 2 }}
          >
            Application Timeline
          </Typography>

          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>

              <Typography>
                📝 Applied On:
                {' '}
                {new Date(
                  selectedAdmission.createdAt
                ).toLocaleString()}
              </Typography>

              {selectedAdmission.updatedAt && (
                <Typography sx={{ mt: 1 }}>
                  🔄 Last Updated:
                  {' '}
                  {new Date(
                    selectedAdmission.updatedAt
                  ).toLocaleString()}
                </Typography>
              )}

              <Typography sx={{ mt: 1 }}>
                📌 Current Status:
                {' '}
                {selectedAdmission.applicationStatus}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                💳 Payment Status:
                {' '}
                {selectedAdmission.paymentStatus}
              </Typography>

            </CardContent>
          </Card>
        </Grid>

      </Grid>

    )}

  </DialogContent>

  <DialogActions>

    {selectedAdmission?.applicationStatus === 'Pending' && (
      <>
        <Button
          color="error"
          variant="outlined"
          onClick={() =>
            handleReject(selectedAdmission.applicationId)
          }
        >
          Reject
        </Button>

        <Button
          color="success"
          variant="contained"
          onClick={() =>
            handleApprove(selectedAdmission.applicationId)
          }
        >
          Approve & Create Student
        </Button>
      </>
    )}
{selectedAdmission &&
 (
   selectedAdmission.paymentStatus ===
     'Submitted' ||
   selectedAdmission.applicationStatus ===
     'Approved'
 ) && (
  <Button
    variant="contained"
    color="primary"
    onClick={() =>
      handleDownloadReceipt(
        selectedAdmission.applicationNumber
      )
    }
  >
    Download Receipt PDF
  </Button>
)}
    <Button onClick={handleCloseDialog}>
      Close
    </Button>

  </DialogActions>

</Dialog>

    </Box>
  );
};

export default Admissions;